import React, { useState } from 'react';

import { Button } from 'components/common';
import { AuTable } from 'components/DTA';

import { DroneData } from 'types/forms/droneRegistrationTypes';

import './DroneTable.scss';

export interface DroneTableProps {
  caption: string | React.ReactNode;
  droneData: any;
  openDroneEdit: (id: number) => void;
  removeDroneToggle: (id: number) => void;
  type?: string;
  pageSize?: number;
}

const DroneTable: React.FC<DroneTableProps> = ({ caption, droneData, openDroneEdit, removeDroneToggle, type = 'registration', pageSize }) => {
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageLink1, setPageLink1] = useState<number>(-1);
  const [pageLink2, setPageLink2] = useState<number>(0);
  const [pageLink3, setPageLink3] = useState<number>(1);
  const [pageLink4, setPageLink4] = useState<number>(2);
  const [pageLink5, setPageLink5] = useState<number>(3);
  const nameHeader = {
    title: 'Drone',
    key: 'name',
    render: (data: string, row: DroneData) => {
      return <>
        <Button link className='no-padding' onClick={() => openDroneEdit(row.id)}>{data}</Button>
        {(row.modelUpdateRequired || row.serialNumberUpdateRequired) &&
          <span style={{ marginLeft: '.2rem' }} className='badge badge-update'>Updates required</span>
        }
        {row.updatesSaved &&
          <span style={{ marginLeft: '.2rem' }} className='badge badge-success'>Updates saved</span>
        }
      </>;
    }
  };
  const serialNumberHeader = {
    title: 'Serial number',
    key: 'serialNumber'
  };
  const weightClassHeader = {
    title: 'Weight class',
    key: 'weightClass',
    render: (data: string, row: DroneData) => (
      row.name
        ? <>{data}</>
        : <b style={{ float: 'right' }}>Total:</b>
    )
  };
  const expiryDateHeader = {
    title: 'Expiry date',
    key: 'validUntil',
    render: (expiryDate: string, row: DroneData) => {
      if (expiryDate) {
        if (row.validUntilMessage && row.validUntilMessageLevel) {
          return <>
            {expiryDate}<span style={{ marginLeft: '.2rem' }} className={`badge badge-${row.validUntilMessageLevel}`}>{row.validUntilMessage}</span>
          </>;
        }
        else {
          return <>
            {expiryDate}
          </>;
        }
      }
      return <></>;
    }

  };
  const newExpiryDateHeader = {
    title: 'New expiry date',
    key: 'renewedValidUntil',
    render: (data: string, row: DroneData) => (
      row.name
        ? <>{data}</>
        : <b style={{ float: 'right' }}>Total:</b>
    )
  };
  const feeHeader = {
    title: 'Fee',
    key: 'fee',
    render: (data: number, row: DroneData) => (
      row.name
        ? <>${data?.toFixed(2)}</>
        : <b>${data?.toFixed(2)}</b>
    )
  };
  const removeHeader = {
    key: 'remove',
    render: (data: string, row: DroneData) =>
      row.name &&
      <Button link className='no-padding' onClick={() => removeDroneToggle(row.id)}>Remove</Button>
  };
  let headers = [nameHeader, serialNumberHeader, weightClassHeader, feeHeader, removeHeader];
  if (type === 'renewal') {
    headers = [nameHeader, serialNumberHeader, expiryDateHeader, newExpiryDateHeader, feeHeader, removeHeader];
  }

  const processPageChange = (pageIndex: number) => {
    setPageIndex(pageIndex);
    setPageLink1(pageIndex - 1);
    setPageLink2(pageIndex);
    setPageLink3(pageIndex + 1);
    setPageLink4(pageIndex + 2);
    setPageLink5(pageIndex + 3);
  };

  return (
    <>
      <AuTable
        caption={caption}
        headers={headers}
        data={pageSize && droneData.length > pageSize ? droneData.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize) : droneData}
        style={pageSize && droneData.length > pageSize ? { marginTop: '3rem', marginBottom: '1rem' } : { marginTop: '3rem', marginBottom: '5rem' }}
        striped />
      {pageSize && droneData.length > pageSize &&
        <>
          <div className='paginator'>
            <Button link onClick={() => processPageChange(pageIndex > 0 ? pageIndex - 1 : 0)} disabled={pageIndex === 0}>«</Button>

            <Button link className='hypen' onClick={() => processPageChange(pageLink1 - 1)} disabled={pageLink1 < 1 || pageIndex === (pageLink1 - 1)}>{pageLink1 > 0 ? pageLink1 : '-'}</Button>
            <Button link className='hypen' onClick={() => processPageChange(pageLink2 - 1)} disabled={pageLink2 < 1 || pageIndex === (pageLink2 - 1)}>{pageLink2 > 0 ? pageLink2 : '-'}</Button>
            <Button link className='active' onClick={() => processPageChange(pageLink3 - 1)} disabled={pageIndex === (pageLink3 - 1)}>{pageLink3}</Button>
            <Button link className='hypen' onClick={() => processPageChange(pageLink4 - 1)} disabled={pageLink4 > Math.ceil(droneData.length / pageSize)}>{pageLink4 <= Math.ceil(droneData.length / pageSize) ? pageLink4 : '-'}</Button>
            <Button link className='hypen' onClick={() => processPageChange(pageLink5 - 1)} disabled={pageLink5 > Math.ceil(droneData.length / pageSize)}>{pageLink5 <= Math.ceil(droneData.length / pageSize) ? pageLink5 : '-'}</Button>

            <Button link onClick={() => processPageChange(pageIndex < (droneData.length / pageSize) - 1 ? pageIndex + 1 : pageIndex)} disabled={(pageIndex + 1) >= Math.ceil(droneData.length / pageSize)}>»</Button>
          </div>
        </>
      }
    </>
  );
};

export default DroneTable;